import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import {
  adminListReceiptJobs,
  adminProcessReceiptJob,
  adminProcessReceiptQueue,
  adminReconcilePayfast,
} from "@/lib/api/payments-admin.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Play, RefreshCw, ShieldCheck } from "lucide-react";

export const Route = createFileRoute(
  "/_authenticated/portal/admin/receipts-queue",
)({
  component: ReceiptsQueueAdmin,
});

type Job = {
  id: string;
  reference: string;
  kind: "credit" | "contract";
  buyer_email: string;
  attempts: number;
  max_attempts: number;
  status: "pending" | "sent" | "failed" | "cancelled";
  last_error: string | null;
  next_attempt_at: string | null;
  sent_at: string | null;
  triggered_by: string;
  created_at: string;
};

function statusClass(s: Job["status"]) {
  switch (s) {
    case "sent":
      return "bg-emerald-100 text-emerald-800";
    case "failed":
      return "bg-rose-100 text-rose-800";
    case "cancelled":
      return "bg-zinc-100 text-zinc-700";
    default:
      return "bg-amber-100 text-amber-800";
  }
}

function ReceiptsQueueAdmin() {
  const list = useServerFn(adminListReceiptJobs);
  const processOne = useServerFn(adminProcessReceiptJob);
  const drainAll = useServerFn(adminProcessReceiptQueue);
  const reconcile = useServerFn(adminReconcilePayfast);
  const qc = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<
    "all" | "pending" | "sent" | "failed" | "cancelled"
  >("all");
  const [search, setSearch] = useState("");

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["receipt-jobs-admin"],
    queryFn: () => list(),
    refetchInterval: 30_000,
  });

  const drainMut = useMutation({
    mutationFn: () => drainAll(),
    onSuccess: (r) => {
      toast.success(
        `Drained ${r.picked}: ${r.sent} sent, ${r.retried} retry, ${r.failed} failed`,
      );
      qc.invalidateQueries({ queryKey: ["receipt-jobs-admin"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Drain failed"),
  });

  const reconMut = useMutation({
    mutationFn: () => reconcile(),
    onSuccess: (r) => {
      toast.success(
        `Reconciled — enqueued ${r.paid_no_receipt.enqueued}, repaired ${r.complete_unpaid.reconciled}, sent ${r.queue.sent}`,
      );
      qc.invalidateQueries({ queryKey: ["receipt-jobs-admin"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Reconcile failed"),
  });

  const oneMut = useMutation({
    mutationFn: (jobId: string) => processOne({ data: { jobId } }),
    onSuccess: (r) => {
      if (r.ok && r.outcome === "sent") toast.success(`Receipt sent (${r.reference})`);
      else if (r.outcome === "retry") toast.warning(`Still failing — backed off (${r.reference})`);
      else toast.info(`Outcome: ${r.outcome}`);
      qc.invalidateQueries({ queryKey: ["receipt-jobs-admin"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const rows: Job[] = (data?.rows ?? []) as unknown as Job[];
  const filtered = rows.filter((j) => {
    if (statusFilter !== "all" && j.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !j.reference.toLowerCase().includes(q) &&
        !j.buyer_email.toLowerCase().includes(q)
      )
        return false;
    }
    return true;
  });
  const counts = {
    pending: rows.filter((r) => r.status === "pending").length,
    failed: rows.filter((r) => r.status === "failed").length,
    sent: rows.filter((r) => r.status === "sent").length,
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl text-navy">Receipt retry queue</h1>
          <p className="text-sm text-muted-foreground">
            PayFast receipts that failed to send first time. Auto-retries every
            5 min with exponential backoff. Drain or process individual jobs
            here. Reconciliation runs every 15 min in the background.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`mr-1 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button size="sm" onClick={() => drainMut.mutate()} disabled={drainMut.isPending}>
            {drainMut.isPending ? "Draining…" : "Drain queue now"}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => reconMut.mutate()} disabled={reconMut.isPending}>
            <ShieldCheck className="mr-1 h-4 w-4" />
            {reconMut.isPending ? "Reconciling…" : "Reconcile PayFast"}
          </Button>
        </div>
      </header>

      <section className="grid gap-3 sm:grid-cols-3">
        <Stat label="Pending" value={counts.pending} tone="amber" />
        <Stat label="Failed (give-up)" value={counts.failed} tone="rose" />
        <Stat label="Sent" value={counts.sent} tone="emerald" />
      </section>

      <section className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card p-3">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs text-muted-foreground">Search</label>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Reference or email…"
          />
        </div>
        <div className="w-44">
          <label className="text-xs text-muted-foreground">Status</label>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </section>

      <section className="overflow-hidden rounded-xl border border-border bg-card">
        {isLoading ? (
          <p className="p-6 text-center text-sm text-muted-foreground">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">
            No jobs match. The queue is healthy.
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-muted text-left uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2">Reference</th>
                <th className="px-3 py-2">Buyer email</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Attempts</th>
                <th className="px-3 py-2">Next retry</th>
                <th className="px-3 py-2">Last error</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((j) => (
                <tr key={j.id} className="align-top">
                  <td className="px-3 py-2 whitespace-nowrap">
                    {new Date(j.created_at).toLocaleString("en-ZA")}
                  </td>
                  <td className="px-3 py-2 font-mono">{j.reference}</td>
                  <td className="px-3 py-2">{j.buyer_email}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${statusClass(j.status)}`}>
                      {j.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 tabular-nums">
                    {j.attempts}/{j.max_attempts}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {j.next_attempt_at
                      ? new Date(j.next_attempt_at).toLocaleString("en-ZA")
                      : "—"}
                  </td>
                  <td className="px-3 py-2 max-w-[260px] text-rose-700">
                    {j.last_error ?? ""}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {j.status === "pending" || j.status === "failed" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={oneMut.isPending && oneMut.variables === j.id}
                        onClick={() => oneMut.mutate(j.id)}
                      >
                        {oneMut.isPending && oneMut.variables === j.id ? (
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        ) : (
                          <Play className="mr-1 h-3 w-3" />
                        )}
                        Process now
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "amber" | "rose" | "emerald";
}) {
  const cls =
    tone === "amber"
      ? "border-amber-200 bg-amber-50 text-amber-900"
      : tone === "rose"
        ? "border-rose-200 bg-rose-50 text-rose-900"
        : "border-emerald-200 bg-emerald-50 text-emerald-900";
  return (
    <div className={`rounded-xl border p-4 ${cls}`}>
      <p className="text-xs uppercase tracking-wide opacity-70">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}
